import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Link,
  Card
} from '@mui/material';
import { Add, Edit, Visibility, Search, Delete, Upload, Link as LinkIcon, OpenInNew } from '@mui/icons-material';
import { companiesApi, CompanyUserInfo } from '../services/companies';
import { documentsService } from '../services/documents';
import { companyWalletsApi, CompanyWallet } from '../services/companyWallets';
import { walletsApi, Wallet } from '../services/wallets';
import { CompanyList, Company, CompanyForm, Document, CompanyDocument } from '../types';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';

// List of countries
const COUNTRIES = [
  'Russia', 'Ukraine', 'Belarus', 'Kazakhstan', 'Uzbekistan', 'Kyrgyzstan',
  'Tajikistan', 'Turkmenistan', 'Moldova', 'Azerbaijan', 'Armenia', 'Georgia',
  'Latvia', 'Lithuania', 'Estonia', 'Poland', 'Czech Republic', 'Slovakia', 'Hungary',
  'Romania', 'Bulgaria', 'Serbia', 'Croatia', 'Slovenia', 'Germany',
  'France', 'Italy', 'Spain', 'United Kingdom', 'Netherlands', 'Belgium',
  'Switzerland', 'Austria', 'Sweden', 'Norway', 'Denmark', 'Finland',
  'United States', 'Canada', 'Mexico', 'Brazil', 'Argentina', 'Chile', 'Colombia',
  'China', 'Japan', 'South Korea', 'India', 'Thailand', 'Vietnam',
  'Singapore', 'Malaysia', 'Indonesia', 'Philippines', 'Australia', 'New Zealand',
  'Turkey', 'Israel', 'United Arab Emirates', 'Saudi Arabia', 'Egypt', 'South Africa',
  'Union of Сomoros', 'Republic of Mauritius', 'Saint Lucia'
].sort();

const CompaniesPage: React.FC = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [companies, setCompanies] = useState<CompanyList[]>([]);
  const [allCompanies, setAllCompanies] = useState<CompanyList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false); // Режим просмотра (только чтение)
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [managers, setManagers] = useState<CompanyUserInfo[]>([]);
  const [clients, setClients] = useState<CompanyUserInfo[]>([]);
  const [partners, setPartners] = useState<CompanyUserInfo[]>([]);
  const [csms, setCSMs] = useState<CompanyUserInfo[]>([]);
  const [formLoading, setFormLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [companyDocuments, setCompanyDocuments] = useState<CompanyDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [newDocumentUrl, setNewDocumentUrl] = useState('');
  const [newDocumentId, setNewDocumentId] = useState<number | ''>('');
  const [newDocumentNotes, setNewDocumentNotes] = useState('');
  const [newDocumentExpiresAt, setNewDocumentExpiresAt] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [editingExpiresDoc, setEditingExpiresDoc] = useState<CompanyDocument | null>(null);
  const [editExpiresAt, setEditExpiresAt] = useState('');
  const [expiresSaving, setExpiresSaving] = useState(false);
  const [companyWallets, setCompanyWallets] = useState<CompanyWallet[]>([]);
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [newWalletId, setNewWalletId] = useState<number | ''>('');
  const [newWalletComment, setNewWalletComment] = useState('');
  
  // Form state
  const [formData, setFormData] = useState<CompanyForm>({
    name: '',
    country: '',
    site: '',
    active: true,
    payment_method: undefined,
    manager_id: undefined,
    partner_id: undefined,
    partner_condition1: '',
    client_ids: [],
    main_contact_client_id: undefined,
    csm_id: undefined
  });

  const loadCompanies = async (search?: string) => {
    try {
      setLoading(true);
      setError('');
      const data = await companiesApi.getCompanies(search);
      setAllCompanies(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error loading companies');
    } finally {
      setLoading(false);
    }
  };

  const filterCompanies = (data: CompanyList[], search: string, onlyActive: boolean) => {
    let filtered = [...data];
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(company => 
        company.name.toLowerCase().includes(searchLower)
      );
    }
    
    // Active filter
    if (onlyActive) {
      filtered = filtered.filter(company => company.active);
    }
    
    setCompanies(filtered);
  };

  const handleSearch = () => {
    filterCompanies(allCompanies, searchTerm, showOnlyActive);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const loadManagers = async () => {
    try {
      const data = await companiesApi.getManagers();
      setManagers(data);
    } catch (err: any) {
      console.error('Error loading managers:', err);
    }
  };

  const loadClients = async () => {
    try {
      const data = await companiesApi.getClients();
      setClients(data);
    } catch (err: any) {
      console.error('Error loading clients:', err);
    }
  };

  const loadPartners = async () => {
    try {
      const data = await companiesApi.getPartners();
      setPartners(data);
    } catch (err: any) {
      console.error('Error loading partners:', err);
    }
  };

  const loadCSMs = async () => {
    try {
      const data = await companiesApi.getCSMs();
      setCSMs(data);
    } catch (err: any) {
      console.error('Error loading CSMs:', err);
    }
  };

  const loadCompanyDetails = async (id: number) => {
    try {
      setFormLoading(true);
      const company = await companiesApi.getCompany(id);
      setSelectedCompany(company);
      return company;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error loading company data');
      return null;
    } finally {
      setFormLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const data = await documentsService.getDocuments();
      setDocuments(data);
    } catch (err: any) {
      console.error('Error loading document types:', err);
    }
  };

  const loadCompanyDocuments = async (companyId: number) => {
    try {
      setDocumentsLoading(true);
      const data = await documentsService.getCompanyDocuments(companyId);
      setCompanyDocuments(data);
    } catch (err: any) {
      console.error('Error loading company documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleAddDocumentByUrl = async () => {
    if (!selectedCompany || !newDocumentId || !newDocumentUrl) {
      setError('Please fill in all fields');
      return;
    }
    try {
      await documentsService.createCompanyDocument(selectedCompany.id, {
        document_id: newDocumentId as number,
        url: newDocumentUrl,
        notes: newDocumentNotes || undefined,
        expires_at: newDocumentExpiresAt || null,
        is_verified: false
      });
      setNewDocumentUrl('');
      setNewDocumentId('');
      setNewDocumentNotes('');
      setNewDocumentExpiresAt('');
      await loadCompanyDocuments(selectedCompany.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error adding document');
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedCompany || !newDocumentId || !uploadFile) {
      setError('Please select document type and file');
      return;
    }
    try {
      await documentsService.uploadCompanyDocument(
        selectedCompany.id,
        newDocumentId as number,
        uploadFile,
        newDocumentNotes || undefined,
        newDocumentExpiresAt || null
      );
      setUploadFile(null);
      setNewDocumentId('');
      setNewDocumentNotes('');
      setNewDocumentExpiresAt('');
      await loadCompanyDocuments(selectedCompany.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error uploading file');
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!selectedCompany) return;
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentsService.deleteCompanyDocument(documentId);
        await loadCompanyDocuments(selectedCompany.id);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Error deleting document');
      }
    }
  };

  const openEditExpires = (doc: CompanyDocument) => {
    setEditingExpiresDoc(doc);
    setEditExpiresAt(doc.expires_at ? doc.expires_at.slice(0, 10) : '');
  };

  const handleSaveExpires = async () => {
    if (!selectedCompany || !editingExpiresDoc) return;
    try {
      setExpiresSaving(true);
      await documentsService.updateCompanyDocument(editingExpiresDoc.id, {
        expires_at: editExpiresAt || null
      });
      setEditingExpiresDoc(null);
      setEditExpiresAt('');
      await loadCompanyDocuments(selectedCompany.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error updating expiration date');
    } finally {
      setExpiresSaving(false);
    }
  };

  useEffect(() => {
    loadCompanies();
    loadManagers();
    loadClients();
    loadPartners();
    loadCSMs();
    loadDocuments();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadCompanyDocuments(selectedCompany.id);
      loadCompanyWallets(selectedCompany.id);
    } else {
      setCompanyDocuments([]);
      setCompanyWallets([]);
      setWallets([]);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      loadAvailableWallets(selectedCompany.payment_method);
    }
  }, [selectedCompany?.payment_method, companyWallets.length]);

  const loadCompanyWallets = async (companyId: number) => {
    try {
      setWalletsLoading(true);
      const data = await companyWalletsApi.getCompanyWallets(companyId);
      setCompanyWallets(data);
    } catch (err: any) {
      console.error('Error loading company wallets:', err);
      setError(err.response?.data?.detail || 'Error loading company wallets');
    } finally {
      setWalletsLoading(false);
    }
  };

  const loadAvailableWallets = async (paymentMethod?: string): Promise<Wallet[]> => {
    try {
      if (!paymentMethod) {
        setWallets([]);
        return [];
      }
      
      let walletType: 'CRYPTO' | 'BANK' | undefined;
      if (paymentMethod === 'bank_transfer') {
        walletType = 'BANK';
      } else if (paymentMethod === 'crypto_transfer') {
        walletType = 'CRYPTO';
      }
      
      const data = await walletsApi.getWallets(0, 1000, walletType, true);
      // Filter out wallets already added to company
      const addedWalletIds = companyWallets.map(cw => cw.wallet_id);
      const filtered = data.filter(w => !addedWalletIds.includes(w.id));
      setWallets(filtered);
      return filtered;
    } catch (err: any) {
      console.error('Error loading wallets:', err);
      return [];
    }
  };

  const handleAddWallet = async () => {
    if (!selectedCompany || !newWalletId) {
      setError('Please select a wallet');
      return;
    }
    try {
      await companyWalletsApi.createCompanyWallet({
        company_id: selectedCompany.id,
        wallet_id: newWalletId as number,
        comment: newWalletComment || undefined
      });
      setNewWalletId('');
      setNewWalletComment('');
      await loadCompanyWallets(selectedCompany.id);
      await loadAvailableWallets(selectedCompany.payment_method);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error adding wallet');
    }
  };

  const handleDeleteWallet = async (companyWalletId: number) => {
    if (!selectedCompany) return;
    if (window.confirm('Are you sure you want to remove this wallet?')) {
      try {
        await companyWalletsApi.deleteCompanyWallet(companyWalletId);
        await loadCompanyWallets(selectedCompany.id);
        await loadAvailableWallets(selectedCompany.payment_method);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Error removing wallet');
      }
    }
  };

  useEffect(() => {
    filterCompanies(allCompanies, searchTerm, showOnlyActive);
  }, [allCompanies, searchTerm, showOnlyActive]);

  const handleOpenCompany = async (companyId: number) => {
    const company = await loadCompanyDetails(companyId);
    if (company) {
      // Проверяем права на редактирование
      const canEdit = !isClient;
      setIsViewMode(!canEdit); // Если нет прав - режим просмотра
      
      // Устанавливаем вкладку только после загрузки данных
      // Для клиентов всегда показываем вкладку 0 (Main Information), так как Wallets скрыта
      setActiveTab(0);
      setFormData({
        name: company.name,
        country: company.country,
        site: company.site || '',
        active: company.active,
        payment_method: company.payment_method,
        manager_id: company.manager?.id,
        partner_id: company.partner?.id,
        partner_condition1: company.partner?.partnership_condition_1 || '',
        client_ids: company.clients.map(c => c.id),
        main_contact_client_id: company.main_contact_client?.id,
        csm_id: company.csm?.id
      });
      setOpenEditDialog(true);
    }
  };

  const handleAddCompany = () => {
    setSelectedCompany(null);
    setIsViewMode(false);
    setActiveTab(0);
    setFormData({
      name: '',
      country: '',
      site: '',
      active: true,
      payment_method: undefined,
      manager_id: undefined,
      partner_id: undefined,
      partner_condition1: '',
      client_ids: [],
      main_contact_client_id: undefined,
      csm_id: undefined
    });
    setOpenEditDialog(true);
  };

  const handleSaveCompany = async () => {
    try {
      setFormLoading(true);
      setError('');
      
      if (selectedCompany) {
        // Update existing company
        await companiesApi.updateCompany(selectedCompany.id, formData);
      } else {
        // Create new company
        await companiesApi.createCompany(formData);
      }
      
      setOpenEditDialog(false);
      setIsViewMode(false);
      await loadCompanies();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error saving company');
    } finally {
      setFormLoading(false);
    }
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Companies
        </Typography>
        {!isClient && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddCompany}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.25,
            }}
          >
            Add Company
          </Button>
        )}
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
          }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <Box sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by company name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              filterCompanies(allCompanies, e.target.value, showOnlyActive);
            }}
            onKeyPress={handleSearchKeyPress}
            size="small"
            sx={{ 
              flexGrow: 1,
              minWidth: 250,
            }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showOnlyActive}
                onChange={(e) => {
                  setShowOnlyActive(e.target.checked);
                  filterCompanies(allCompanies, searchTerm, e.target.checked);
                }}
              />
            }
            label="Show only active"
            sx={{ mr: 2 }}
          />
          {searchTerm && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearchTerm('');
                filterCompanies(allCompanies, '', showOnlyActive);
              }}
              sx={{ borderRadius: 2 }}
            >
              Reset
            </Button>
          )}
        </Box>
      </Card>

      <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Balance</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>CSM</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Payment Method</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No companies found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company) => (
                  <TableRow 
                    key={company.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(37, 99, 235, 0.02)',
                        cursor: 'pointer',
                      },
                    }}
                    onClick={() => handleOpenCompany(company.id)}
                  >
                    <TableCell>{company.id}</TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: 'primary.main',
                        }}
                      >
                        {company.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {company.country}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={company.active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          background: company.active 
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: company.balance >= 0 ? 'success.main' : 'error.main',
                        }}
                      >
                        ${company.balance?.toFixed(2) || '0.00'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {company.csm_email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {company.payment_method === 'bank_transfer' ? (
                        <Chip 
                          label="Bank Transfer" 
                          size="small"
                          sx={{ 
                            background: 'rgba(37, 99, 235, 0.1)',
                            color: '#2563eb',
                            fontWeight: 500,
                          }}
                        />
                      ) : company.payment_method === 'crypto_transfer' ? (
                        <Chip 
                          label="Crypto Transfer" 
                          size="small"
                          sx={{ 
                            background: 'rgba(139, 92, 246, 0.1)',
                            color: '#8b5cf6',
                            fontWeight: 500,
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>


      {/* Edit/Create/View Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          // Не сбрасываем isViewMode для клиентов, чтобы избежать кратковременного отображения редактируемых полей
          if (!isClient) {
            setIsViewMode(false);
          }
          // Переключаем вкладку только после закрытия модального окна, чтобы избежать визуального переключения
          setTimeout(() => setActiveTab(0), 100);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedCompany ? (isViewMode ? 'View Company' : 'Edit Company') : 'Add Company'}
        </DialogTitle>
        <DialogContent>
          {formLoading && !selectedCompany ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              {selectedCompany && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs 
                    value={isClient && activeTab === 2 ? 0 : activeTab} 
                    onChange={(e, newValue) => {
                      // Если клиент пытается перейти на вкладку Wallets (2), перенаправляем на Main Information (0)
                      if (isClient && newValue === 2) {
                        setActiveTab(0);
                      } else {
                        setActiveTab(newValue);
                      }
                    }}
                  >
                    <Tab label="Main Information" />
                    <Tab label="Documents" />
                    {!isClient && <Tab label="Wallets" />}
                  </Tabs>
                </Box>
              )}
              
              {activeTab === 0 && (
                <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={isViewMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      label="Country"
                      disabled={isViewMode}
                    >
                      {COUNTRIES.map((country) => (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={formData.site}
                    onChange={(e) => setFormData({ ...formData, site: e.target.value })}
                    disabled={isViewMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={formData.payment_method || ''}
                      onChange={(e) => setFormData({ ...formData, payment_method: e.target.value as string | undefined })}
                      label="Payment Method"
                      disabled={isViewMode}
                    >
                      <MenuItem value="">Not selected</MenuItem>
                      <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                      <MenuItem value="crypto_transfer">Crypto Transfer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        disabled={isViewMode}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                {selectedCompany && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f5f5f5' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Company Balance
                      </Typography>
                      <Typography variant="h5" color={selectedCompany.balance >= 0 ? 'success.main' : 'error.main'}>
                        ${selectedCompany.balance?.toFixed(2) || '0.00'}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>AE (Account Executive)</InputLabel>
                    <Select
                      value={formData.manager_id || ''}
                      onChange={(e) => setFormData({ ...formData, manager_id: e.target.value as number | undefined })}
                      label="AE (Account Executive)"
                      disabled={isViewMode}
                    >
                      <MenuItem value="">Not selected</MenuItem>
                      {managers.map((manager) => (
                        <MenuItem key={manager.id} value={manager.id}>
                          {manager.first_name} {manager.last_name} ({manager.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Partner</InputLabel>
                    <Select
                      value={formData.partner_id || ''}
                      onChange={(e) => setFormData({ ...formData, partner_id: e.target.value as number | undefined })}
                      label="Partner"
                      disabled={isViewMode}
                    >
                      <MenuItem value="">Not selected</MenuItem>
                      {partners.map((partner) => (
                        <MenuItem key={partner.id} value={partner.id}>
                          {partner.first_name} {partner.last_name} ({partner.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {formData.partner_id && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Partnership Condition"
                      value={formData.partner_condition1}
                      onChange={(e) => setFormData({ ...formData, partner_condition1: e.target.value })}
                      multiline
                      rows={2}
                      disabled={isViewMode}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Main Contact</InputLabel>
                    <Select
                      value={formData.main_contact_client_id || ''}
                      onChange={(e) => setFormData({ ...formData, main_contact_client_id: e.target.value as number | undefined })}
                      label="Main Contact"
                      disabled={isViewMode}
                    >
                      <MenuItem value="">Not selected</MenuItem>
                      {clients.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.first_name} {client.last_name} ({client.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {(selectedCompany?.main_contact_client || formData.main_contact_client_id) && (() => {
                  const mainContact = formData.main_contact_client_id 
                    ? clients.find(c => c.id === formData.main_contact_client_id)
                    : selectedCompany?.main_contact_client;
                  
                  return mainContact ? (
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#f5f5f5' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Main Contact Information
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Name:
                            </Typography>
                            <Typography variant="body1">
                              {mainContact.first_name} {mainContact.last_name}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Email:
                            </Typography>
                            <Typography variant="body1">
                              {mainContact.email}
                            </Typography>
                          </Grid>
                          {mainContact.phone && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Phone:
                              </Typography>
                              <Typography variant="body1">
                                {mainContact.phone}
                              </Typography>
                            </Grid>
                          )}
                          {mainContact.address_1 && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                Address:
                              </Typography>
                              <Typography variant="body1">
                                {mainContact.address_1}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  ) : null;
                })()}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>CSM</InputLabel>
                    <Select
                      value={formData.csm_id || ''}
                      onChange={(e) => setFormData({ ...formData, csm_id: e.target.value as number | undefined })}
                      label="CSM"
                      disabled={isViewMode}
                    >
                      <MenuItem value="">Not selected</MenuItem>
                      {csms.map((csm) => (
                        <MenuItem key={csm.id} value={csm.id}>
                          {csm.first_name} {csm.last_name} ({csm.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Representative</InputLabel>
                    <Select
                      multiple
                      value={formData.client_ids}
                      onChange={(e) => setFormData({ ...formData, client_ids: e.target.value as number[] })}
                      label="Representative"
                      disabled={isViewMode}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as number[]).map((value) => {
                            // Используем selectedCompany.clients для отображения, если доступно, иначе clients
                            const client = selectedCompany?.clients?.find(c => c.id === value) || clients.find(c => c.id === value);
                            return client ? (
                              <Chip key={value} label={`${client.first_name} ${client.last_name}`} size="small" />
                            ) : null;
                          })}
                        </Box>
                      )}
                    >
                      {/* Всегда используем полный список clients для возможности добавления новых представителей */}
                      {clients.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.first_name} {client.last_name} ({client.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              )}
              
              {activeTab === 1 && selectedCompany && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Company Documents
                  </Typography>
                  
                  {/* Add document by URL */}
                  {!isClient && !isViewMode && (
                  <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Add Document by URL
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>Document Type</InputLabel>
                          <Select
                            value={newDocumentId}
                            onChange={(e) => setNewDocumentId(e.target.value as number | '')}
                            label="Document Type"
                          >
                            <MenuItem value="">Select type</MenuItem>
                            {documents.map((doc) => (
                              <MenuItem key={doc.id} value={doc.id}>
                                {doc.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Document URL"
                          value={newDocumentUrl}
                          onChange={(e) => setNewDocumentUrl(e.target.value)}
                          placeholder="https://..."
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleAddDocumentByUrl}
                          disabled={!newDocumentId || !newDocumentUrl}
                          startIcon={<LinkIcon />}
                          sx={{ height: '56px' }}
                        >
                          Add
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Expiration Date (optional)"
                          type="date"
                          value={newDocumentExpiresAt}
                          onChange={(e) => setNewDocumentExpiresAt(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Notes (optional)"
                          value={newDocumentNotes}
                          onChange={(e) => setNewDocumentNotes(e.target.value)}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  )}
                  
                  {/* Upload file */}
                  {!isClient && !isViewMode && (
                  <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Upload File
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                          <InputLabel>Document Type</InputLabel>
                          <Select
                            value={newDocumentId}
                            onChange={(e) => setNewDocumentId(e.target.value as number | '')}
                            label="Document Type"
                          >
                            <MenuItem value="">Select type</MenuItem>
                            {documents.map((doc) => (
                              <MenuItem key={doc.id} value={doc.id}>
                                {doc.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          startIcon={<Upload />}
                          sx={{ height: '56px' }}
                        >
                          {uploadFile ? uploadFile.name : 'Select File'}
                          <input
                            type="file"
                            hidden
                            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                          />
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleUploadDocument}
                          disabled={!newDocumentId || !uploadFile}
                          startIcon={<Upload />}
                          sx={{ height: '56px' }}
                        >
                          Upload
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Expiration Date (optional)"
                          type="date"
                          value={newDocumentExpiresAt}
                          onChange={(e) => setNewDocumentExpiresAt(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Notes (optional)"
                          value={newDocumentNotes}
                          onChange={(e) => setNewDocumentNotes(e.target.value)}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  )}
                  
                  {/* Document list */}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Document List
                  </Typography>
                  {documentsLoading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : companyDocuments.length === 0 ? (
                    <Alert severity="info">No documents added</Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Document Type</TableCell>
                            <TableCell>File/URL</TableCell>
                            <TableCell>Size</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Expires</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {companyDocuments.map((doc) => (
                            <TableRow key={doc.id}>
                              <TableCell>{doc.document?.name || `ID: ${doc.document_id}`}</TableCell>
                              <TableCell>
                                {doc.url.startsWith('uploads/') || doc.url.startsWith('/uploads/') || doc.url.startsWith('http') ? (
                                  <Link href={doc.url.startsWith('http') ? doc.url : `http://localhost:8000/${doc.url.startsWith('/') ? doc.url.slice(1) : doc.url}`} target="_blank" rel="noopener noreferrer">
                                    {doc.file_name || 'Open'}
                                    <OpenInNew fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                                  </Link>
                                ) : (
                                  doc.url
                                )}
                              </TableCell>
                              <TableCell>
                                {doc.file_size ? `${(doc.file_size / 1024).toFixed(2)} KB` : '-'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={doc.is_verified ? 'Verified' : 'Not verified'}
                                  color={doc.is_verified ? 'success' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{format(new Date(doc.created_at), 'dd.MM.yyyy')}</TableCell>
                              <TableCell>
                                {doc.expires_at ? format(new Date(doc.expires_at), 'dd.MM.yyyy') : '—'}
                              </TableCell>
                              <TableCell>
                                {!isClient && !isViewMode && (
                                <>
                                <IconButton
                                  size="small"
                                  onClick={() => openEditExpires(doc)}
                                  title="Edit expiration date"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  color="error"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                                </>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {activeTab === 2 && selectedCompany && !isClient && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Company Wallets
                  </Typography>
                  
                  {!selectedCompany.payment_method ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Please set payment method in Main Information tab first
                    </Alert>
                  ) : (
                    <>
                      {/* Add wallet */}
                      {!isClient && !isViewMode && (
                        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            Add Wallet
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth>
                                <InputLabel>Wallet</InputLabel>
                                <Select
                                  value={newWalletId}
                                  onChange={(e) => setNewWalletId(e.target.value as number | '')}
                                  label="Wallet"
                                >
                                  <MenuItem value="">Select wallet</MenuItem>
                                  {wallets.map((wallet) => (
                                    <MenuItem key={wallet.id} value={wallet.id}>
                                      {wallet.name} ({wallet.wallet_type === 'CRYPTO' ? wallet.crypto_currency : 'Bank'})
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                fullWidth
                                label="Comment (optional)"
                                value={newWalletComment}
                                onChange={(e) => setNewWalletComment(e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <Button
                                fullWidth
                                variant="contained"
                                onClick={handleAddWallet}
                                disabled={!newWalletId}
                                startIcon={<Add />}
                                sx={{ height: '56px' }}
                              >
                                Add
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                      
                      {/* Wallet list */}
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Wallet List
                      </Typography>
                      {walletsLoading ? (
                        <Box display="flex" justifyContent="center" p={2}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : companyWallets.length === 0 ? (
                        <Alert severity="info">No wallets added</Alert>
                      ) : (
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Wallet Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Details</TableCell>
                                <TableCell>Comment</TableCell>
                                <TableCell>Added</TableCell>
                                {!isClient && !isViewMode && <TableCell>Actions</TableCell>}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {companyWallets.map((cw) => (
                                <TableRow key={cw.id}>
                                  <TableCell>{cw.wallet?.name || '-'}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={cw.wallet?.wallet_type === 'CRYPTO' ? 'Crypto' : 'Bank'}
                                      color={cw.wallet?.wallet_type === 'CRYPTO' ? 'primary' : 'secondary'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {cw.wallet?.wallet_type === 'CRYPTO' ? (
                                      <Typography variant="body2">
                                        {cw.wallet.crypto_currency || '-'}
                                      </Typography>
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          maxWidth: 200,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                        title={cw.wallet?.bank_details || ''}
                                      >
                                        {cw.wallet?.bank_details || '-'}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>{cw.comment || '-'}</TableCell>
                                  <TableCell>
                                    {format(new Date(cw.created_at), 'dd.MM.yyyy HH:mm')}
                                  </TableCell>
                                  {!isClient && !isViewMode && (
                                    <TableCell>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteWallet(cw.id)}
                                        color="error"
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            // Не сбрасываем isViewMode для клиентов, чтобы избежать кратковременного отображения редактируемых полей и кнопки Save
            if (!isClient) {
              setIsViewMode(false);
            }
            // Переключаем вкладку только после закрытия модального окна, чтобы избежать визуального переключения
            setTimeout(() => setActiveTab(0), 100);
          }} disabled={formLoading}>
            {isViewMode || isClient ? 'Close' : 'Cancel'}
          </Button>
          {!isClient && !isViewMode && (
          <Button
            onClick={handleSaveCompany}
            variant="contained"
            disabled={formLoading || !formData.name || !formData.country}
          >
              {formLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editingExpiresDoc}
        onClose={() => {
          setEditingExpiresDoc(null);
          setEditExpiresAt('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Expiration Date</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Expiration Date"
            type="date"
            value={editExpiresAt}
            onChange={(e) => setEditExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty to clear expiration date"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditingExpiresDoc(null);
              setEditExpiresAt('');
            }}
            disabled={expiresSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveExpires}
            variant="contained"
            disabled={expiresSaving}
          >
            {expiresSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CompaniesPage;